import React from 'react';
import autobind from 'autobind';
import { inject, observer } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';

import { RepresentativeList, RepresentativeListParams } from './RepresentativeList';
import { RepresentativeCardType } from './RepresentativeCard';
import { Ehr } from '@patient/store/ehr/Ehr';
import { representativeService } from '@patient/services/ehr/representativeService';
import { AppState } from '@store/App/App';
import { RepresentativeStatus, RepresentativeType } from 'chaika-enums';
import { patientService } from '@patient/services/patient/patientService';

interface Props extends RepresentativeListParams {}

interface StoreProps {
    ehr: Ehr;
    app: AppState;
}

const injectableStores: (keyof StoreProps)[] = [Ehr.Name, AppState.Name];

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable
    private createMode = false;

    @observable
    private createEhrMode = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const {
            ehr: { representativeList, confidantList },
            cardType,
        } = this.props;

        return React.createElement(RepresentativeList, {
            cardType,
            createEhrMode: this.createEhrMode,
            representativeIds:
                cardType === RepresentativeCardType.Representative
                    ? patientService.getActualPatientIds(representativeList.ids)
                    : patientService.getActualPatientIds(confidantList.ids),
            createMode: this.createMode,
            onCreateNewEhrClick: this.handleCreateEhrClick,
            onCancelCreateEhr: this.handleCancelEhr,
            onAddClick: this.handleAddClick,
            onCancelCreate: this.handleCancelCreate,
            onUpdateRepresentativePerson: this.handleUpdateRepresentativePerson,
            onCreateRepresentativePerson: this.handleCreateRepresentativePerson,
            onConfirmRepresentative: this.handleConfirmRepresentative,
            onRemove: this.handleRemove,
            onRepresentativeTypeChange: this.onRepresentativeTypeChange,
        });
    }

    @autobind
    private handleCreateEhrClick(): void {
        this.createEhrMode = true;
    }

    @autobind
    private handleCancelEhr(): void {
        this.createEhrMode = false;
    }

    @autobind
    private async handleUpdateRepresentativePerson(oldId: string, newId: string): Promise<void> {
        const { app } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        await Promise.all([
            representativeService.remove(oldId),
            representativeService.create({ representativeId: newId }),
        ]);

        app.removeSaveRequest(reqId);
    }

    @autobind
    private async handleRemove(representativeId: string): Promise<void> {
        const { app } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        await representativeService.remove(representativeId);

        app.removeSaveRequest(reqId);
    }

    @autobind
    private handleAddClick(): void {
        this.createMode = true;
    }

    @autobind
    private handleCancelCreate(): void {
        this.createMode = false;
    }

    @autobind
    private async handleCreateRepresentativePerson(representativeId: string, type: RepresentativeType): Promise<void> {
        const { app } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        await representativeService.create({ representativeId, type });
        this.createMode = false;

        app.removeSaveRequest(reqId);
    }

    @autobind
    private async handleConfirmRepresentative(representativeId: string): Promise<void> {
        const { app } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        await representativeService.update(representativeId, {
            status: RepresentativeStatus.Confirmed,
        });

        app.removeSaveRequest(reqId);
    }

    @autobind
    private async onRepresentativeTypeChange(representativeId: string, type: RepresentativeType): Promise<void> {
        const { app } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        await representativeService.update(representativeId, {
            type,
        });

        app.removeSaveRequest(reqId);
    }
}

export const RepresentativeListContainer = inject<Props, StoreProps>(...injectableStores)(Component);
